import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Upload, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CategoryPage = () => {
  const { id } = useParams();
  const [category, setCategory] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');

  useEffect(() => {
    fetchCategory();
    checkAdmin();
  }, [id]);

  const checkAdmin = () => {
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      setIsAdmin(userData.role === 'admin');
    }
  };

  const fetchCategory = async () => {
    try {
      const response = await axios.get(`${API}/categories/${id}`);
      setCategory(response.data);
    } catch (error) {
      console.error('Error fetching category:', error);
      toast.error('Failed to load category');
    }
  };

  const addImage = async () => {
    if (!newImageUrl) return;
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image_url', newImageUrl);
      
      await axios.post(`${API}/admin/categories/${id}/images`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Image added successfully');
      setNewImageUrl('');
      fetchCategory();
    } catch (error) {
      console.error('Error adding image:', error);
      toast.error('Failed to add image');
    }
  };

  const removeImage = async (imageUrl) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image_url', imageUrl);
      
      await axios.delete(`${API}/admin/categories/${id}/images`, {
        data: formData,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Image removed');
      fetchCategory();
    } catch (error) {
      console.error('Error removing image:', error);
      toast.error('Failed to remove image');
    }
  };

  if (!category) {
    return <div className="pt-24 px-6 text-center">Loading...</div>;
  }

  return (
    <div className="pt-32 pb-16 px-6 md:px-12 lg:px-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto"
      >
        <h1 className="text-5xl md:text-6xl font-black mb-6" data-testid="category-title">{category.name}</h1>
        <p className="text-xl text-white/70 mb-12" data-testid="category-description">{category.description}</p>

        {isAdmin && (
          <div className="mb-12 p-6 border border-white/10 bg-white/5">
            <h3 className="text-xl font-bold mb-4">Admin: Add Images</h3>
            <div className="flex gap-4">
              <Input
                type="text"
                placeholder="Enter image URL"
                value={newImageUrl}
                onChange={(e) => setNewImageUrl(e.target.value)}
                data-testid="image-url-input"
                className="flex-1 bg-transparent border-white/20 text-white"
              />
              <Button 
                onClick={addImage} 
                data-testid="add-image-button"
                className="bg-[#FF4500] hover:bg-[#FF4500]/90"
              >
                <Upload size={20} className="mr-2" /> Add Image
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {category.images && category.images.length > 0 ? (
            category.images.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative group overflow-hidden border border-white/10 hover:border-[#FF4500]/50 transition-all"
                data-testid={`gallery-image-${index}`}
              >
                <img 
                  src={image} 
                  alt={`${category.name} ${index + 1}`}
                  className="w-full h-80 object-cover product-image-hover"
                />
                {isAdmin && (
                  <button
                    onClick={() => removeImage(image)}
                    data-testid={`remove-image-${index}`}
                    className="absolute top-4 right-4 bg-red-500 p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 size={20} />
                  </button>
                )}
              </motion.div>
            ))
          ) : (
            <div className="col-span-full text-center py-20 text-white/50" data-testid="no-images">
              <p>No images available yet. Check back soon!</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default CategoryPage;